/* Asiro motion layer
   Subtle scroll reveals and a scroll-linked zoom on two section images.
   Respects prefers-reduced-motion via the html.asiro-motion gate set in theme.liquid. */
(function () {
  'use strict';

  var root = document.documentElement;
  if (!root.classList.contains('asiro-motion')) return;
  if (!('IntersectionObserver' in window)) {
    root.classList.remove('asiro-motion');
    return;
  }

  var REVEAL_SELECTORS = [
    '.asiro-chooser__head',
    '.asiro-chooser__card',
    '.asiro-range__title',
    '.asiro-range__card',
    '.asiro-anatomy__title',
    '.asiro-anatomy__note',
    'product-card'
  ];

  var STAGGER_MS = 90;
  var STAGGER_MAX = 5;

  var ZOOM_SECTIONS = [
    '[id$="__section_Q3Bymf"]',
    '[id$="__section_holder1"]'
  ];
  var ZOOM_START = 1.09;
  var DESKTOP_QUERY = '(min-width: 750px)';

  function isDesktopViewport() {
    return window.matchMedia(DESKTOP_QUERY).matches;
  }

  var revealObserver = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (!entry.isIntersecting) return;
      entry.target.classList.add('is-inview');
      revealObserver.unobserve(entry.target);
    });
  }, { rootMargin: '0px 0px -8% 0px', threshold: 0.12 });

  var anatomyObserver = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (!entry.isIntersecting) return;
      var section = entry.target.closest('.asiro-anatomy');
      if (section) section.classList.add('is-inview');
      anatomyObserver.unobserve(entry.target);
    });
  }, { threshold: 0.35 });

  function blockAncestor(el) {
    var node = el.parentElement;
    while (node && node !== document.body) {
      var d = window.getComputedStyle(node).display;
      if (d !== 'inline' && d !== 'contents') return node;
      node = node.parentElement;
    }
    return el.parentElement;
  }

  var zoomTargets = [];
  var ticking = false;

  function updateZoom() {
    ticking = false;

    if (!isDesktopViewport()) {
      /* On mobile, the zoom-out effect is disabled entirely. A short viewport
         means a screenshot or a slow scroll almost always lands mid-transition,
         which reads as the photo being cropped (it is, transiently, by design,
         on desktop). Simplest fix: no zoom, no clipping, on narrow screens. */
      for (var j = 0; j < zoomTargets.length; j++) {
        zoomTargets[j].style.setProperty('--asiro-zoom', 1);
      }
      return;
    }

    var vh = window.innerHeight || root.clientHeight;
    for (var i = 0; i < zoomTargets.length; i++) {
      var img = zoomTargets[i];
      var rect = img.getBoundingClientRect();
      if (rect.bottom < 0 || rect.top > vh) continue;
      var progress = (vh - rect.top) / (vh * 0.85);
      if (progress < 0) progress = 0;
      if (progress > 1) progress = 1;
      var scale = ZOOM_START - (ZOOM_START - 1) * progress;
      img.style.setProperty('--asiro-zoom', scale.toFixed(4));
    }
  }

  function requestZoom() {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(updateZoom);
  }

  var groupCounter = 0;

  function initReveals(scope) {
    var vh = window.innerHeight || root.clientHeight;
    REVEAL_SELECTORS.forEach(function (sel) {
      var counts = {};
      scope.querySelectorAll(sel).forEach(function (el) {
        if (el.dataset.asiroReveal) return;
        el.dataset.asiroReveal = '1';

        var idx = 0;
        var parent = el.parentElement;
        if (parent) {
          if (!parent.dataset.asiroGroup) parent.dataset.asiroGroup = String(++groupCounter);
          var gid = parent.dataset.asiroGroup;
          idx = counts[gid] || 0;
          counts[gid] = idx + 1;
        }
        el.style.setProperty('--asiro-delay', (Math.min(idx, STAGGER_MAX) * STAGGER_MS) + 'ms');

        var rect = el.getBoundingClientRect();
        var alreadyVisible = rect.top < vh * 0.9 && rect.bottom > 0;
        if (alreadyVisible) {
          el.classList.add('asiro-reveal', 'asiro-reveal--instant', 'is-inview');
          return;
        }
        el.classList.add('asiro-reveal');
        revealObserver.observe(el);
      });
    });
  }

  function initZoom(scope) {
    if (!isDesktopViewport()) return;
    ZOOM_SECTIONS.forEach(function (sel) {
      scope.querySelectorAll(sel).forEach(function (section) {
        var img = section.querySelector('img');
        if (!img || img.dataset.asiroZoom) return;
        img.dataset.asiroZoom = '1';
        var wrap = blockAncestor(img);
        if (wrap) wrap.classList.add('asiro-zoom-wrap');
        img.classList.add('asiro-zoom');
        zoomTargets.push(img);
      });
    });
    requestZoom();
  }

  function initAnatomy(scope) {
    var vh = window.innerHeight || root.clientHeight;
    scope.querySelectorAll('.asiro-anatomy__diagram').forEach(function (el) {
      if (el.dataset.asiroAnatomy) return;
      el.dataset.asiroAnatomy = '1';
      var rect = el.getBoundingClientRect();
      if (rect.top < vh * 0.6 && rect.bottom > 0) {
        var section = el.closest('.asiro-anatomy');
        if (section) section.classList.add('is-inview');
        return;
      }
      anatomyObserver.observe(el);
    });
  }

  function init(scope) {
    scope = scope || document;
    initReveals(scope);
    initZoom(scope);
    initAnatomy(scope);
  }

  var lastWasDesktop = null;

  function handleResize() {
    var nowDesktop = isDesktopViewport();
    if (lastWasDesktop === null) lastWasDesktop = nowDesktop;

    if (nowDesktop && !lastWasDesktop) {
      /* Crossed up into desktop width (e.g. rotating a tablet): pick up any
         zoom sections that were skipped while the page was in mobile mode. */
      init(document);
    }
    lastWasDesktop = nowDesktop;
    requestZoom();
  }

  function start() {
    init(document);
    window.addEventListener('scroll', requestZoom, { passive: true });
    window.addEventListener('resize', handleResize, { passive: true });
    window.addEventListener('load', requestZoom);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  document.addEventListener('shopify:section:load', function (e) {
    init(e.target || document);
  });
})();
